import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { t } from "i18next";
import { getSpotlight } from "../views/utilities/ApiCalls";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";
import Constant from "../views/Constant";
import { ChevronRight } from "lucide-react";

// Add window width hook and truncate function
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return width;
}

function truncateByWords(text, wordLimit) {
  if (!text) return "";
  const words = text.split(" ");
  if (words.length <= wordLimit) return text;
  return words.slice(0, wordLimit).join(" ") + " ";
}

// Add a character-based truncation function
// Truncate by character but do not break words
function truncateByCharsSmart(text, charLimit) {
  if (!text) return "";
  if (text.length <= charLimit) return text;
  let truncated = text.slice(0, charLimit);
  // If the last character isn't a space, backtrack to the last space
  if (text[charLimit] !== " " && truncated.lastIndexOf(" ") > 0) {
    truncated = truncated.slice(0, truncated.lastIndexOf(" "));
  }
  return truncated + " ...";
}

export const SpotlightCarousel1 = () => {
  const history = useHistory();
  const [spotlightData, setSpotlightData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const sliderRef = useRef(null); // Slider reference
  const width = useWindowWidth();

  useEffect(() => {
    const getData = async () => {
      const res = await getSpotlight();
      if (res.data.status === "success") {
        setSpotlightData(res.data.data);
        // console.log("spotlight", res.data.data);
      } else {
        setErrorMessage("Error fetching spotlight");
      }
    };
    getData();
  }, []);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    pauseOnHover: true,
    arrows: false, // We'll use custom arrows
  };

  const handleFeaturedAuthorClick = () => {
    if (spotlightData[0]?.profileLink) {
      window.location.href = spotlightData[0].profileLink;
    } else {
      history.push("/spotlights");
    }
  };

  return (
    <Container>
      <Section>
        {errorMessage && <Error>{errorMessage}</Error>}
        {/* Mobile: Featured Author Card */}
        {spotlightData.length > 0 && spotlightData[0] && (
          <FeaturedAuthorCard onClick={handleFeaturedAuthorClick}>
            <FeaturedAuthorContent>
              <FeaturedAuthorImage
                src={
                  spotlightData[0]?.imageUrl
                    ? `${
                        Constant.apiBasePath
                      }proxy-image?url=${encodeURIComponent(
                        spotlightData[0]?.imageUrl
                      )}`
                    : "https://images.unsplash.com/photo-1485579149621-3123dd979885?q=80&w=1631&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                }
                alt={spotlightData[0]?.author || "Featured Author"}
                crossOrigin="anonymous"
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  flex: 1,
                  gap: "2px",
                }}
              >
                <FeaturedAuthorLabel>FEATURED AUTHOR</FeaturedAuthorLabel>
                <FeaturedAuthorName>
                  {spotlightData[0]?.author || "Featured Author"}
                </FeaturedAuthorName>
              </div>
              <ChevronRight size={20} color="#666" />
            </FeaturedAuthorContent>
          </FeaturedAuthorCard>
        )}
        {/* Desktop: Featured Author Card (Vertical) */}
        <DesktopFeaturedCard>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              gap: "10px",
            }}
          >
            <DesktopLabel>FEATURED AUTHOR</DesktopLabel>

            <DesktopImage
              onClick={() =>
                (window.location.href = `${
                  spotlightData[0]?.profileLink || "#"
                }`)
              }
              src={
                spotlightData[0]?.imageUrl
                  ? `${
                      Constant.apiBasePath
                    }proxy-image?url=${encodeURIComponent(
                      spotlightData[0]?.imageUrl
                    )}`
                  : "https://images.unsplash.com/photo-1485579149621-3123dd979885?q=80&w=1631&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              }
              alt={spotlightData[0]?.author}
              crossOrigin="anonymous"
            />

            <DesktopAuthorName>
              {spotlightData[0]?.author || "Featured Author"}
            </DesktopAuthorName>

            {spotlightData[0]?.quotes?.[0]?.quote && (
              <DesktopQuote>
                "{truncateByCharsSmart(spotlightData[0].quotes[0].quote, 80)}"
              </DesktopQuote>
            )}

            <ViewProfileButton onClick={handleFeaturedAuthorClick}>
              View Profile
            </ViewProfileButton>
          </div>
        </DesktopFeaturedCard>
      </Section>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 10px 10px 0px 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 0px;
  }
`;

const Section = styled.section`
  margin-bottom: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    margin-bottom: 15px;
  }
`;

const Heading = styled.h1`
  font-size: 1.4em;
  color: var(--color-text); // Use theme color
  text-align: center;
  margin-bottom: 15px;
  cursor: pointer;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Error = styled.div`
  color: red;
  text-align: center;
  font-size: 1.3em;
  margin: 20px 0;
`;

const FeaturedAuthorCard = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    width: 100%;
    border-radius: 12px;
    padding: 14px 18px;
    background-color: #e8f5e9;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    flex-direction: column;
    gap: 0px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
  }
`;

const DesktopFeaturedCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #e8f5e9; /* Light green background */
  border-radius: 12px;
  padding: 20px 16px; /* Reduced padding to tighten vertical spacing */
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  border: 1px solid #c8e6c9; /* Slightly darker green border */

  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #2e7d32; /* Dark green label */
  text-transform: uppercase;
  margin-bottom: 2px; /* Reduced spacing below label */
`;

const DesktopImage = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const DesktopAuthorName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1b5e20; /* Darker green/black */
  margin-bottom: 2px; /* Reduced spacing below author name */
`;

const DesktopQuote = styled.div`
  font-size: 14px;
  font-style: italic;
  color: #465c4c;
  line-height: 1.3; /* Slightly tighter line height */
  max-width: 90%;
  margin-bottom: 6px; /* Reduced spacing below quote */
`;

const ViewProfileButton = styled.button`
  background-color: transparent;
  border: 1px solid #2e7d32;
  border-radius: 20px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  color: #2e7d32;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #2e7d32;
    color: white;
  }
`;

// ... Existing Mobile Styled Components (FeaturedAuthorCard, etc.) ...

const FeaturedAuthorLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #2e7d32;
  text-transform: uppercase;
  margin-bottom: 0px;

  @media (max-width: 768px) {
    font-size: 9px;
  }
`;

const FeaturedAuthorContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const FeaturedAuthorImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const FeaturedAuthorName = styled.div`
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: #1b5e20;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;
